import { useRef, type ClipboardEvent } from 'react'
import { toast } from 'sonner'
import {
  Download,
  Eye,
  FileImage,
  FileText,
  Paperclip,
  ReceiptText,
  RefreshCw,
  UploadCloud,
  X,
} from 'lucide-react'
import { type ApInvoicePaymentInput } from '@/features/invoices/types'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getApiErrorMessage } from '@/lib/api-error'
import { formatMoney, formatStatus } from '@/lib/utils'
import { type ApInvoiceDetail } from '@/features/invoices/view-models'
import { compactDate, formatFileSize } from './formatting'
import { type useInvoicePayment } from './use-payment'
import { ComposeTextarea } from '@/components/compose-field'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'

const PAYMENT_STATUSES = [
  'unpaid',
  'scheduled',
  'processing',
  'paid',
  'failed',
  'short_paid',
  'credit_applied',
  'written_off',
] as const

const PAYMENT_RECEIPT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp']

const MAX_PAYMENT_RECEIPT_BYTES = 10 * 1024 * 1024

function validatePaymentReceipt(file: File): string | null {
  if (!PAYMENT_RECEIPT_TYPES.includes(file.type))
    return 'Receipt must be a PDF, PNG, JPEG, or WebP file'
  if (file.size > MAX_PAYMENT_RECEIPT_BYTES) return 'Receipt must be 10 MB or smaller'
  return null
}

export function PaymentReceiptInput({
  file,
  source,
  onChange,
  label = 'Receipt file',
}: {
  file: File | null
  source: 'manual_upload' | 'clipboard_paste'
  onChange: (file: File | null, source: 'manual_upload' | 'clipboard_paste') => void
  label?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const selectFile = (nextFile: File | null, nextSource: 'manual_upload' | 'clipboard_paste') => {
    if (!nextFile) return
    const error = validatePaymentReceipt(nextFile)
    if (error) {
      toast.error(error)
      return
    }
    onChange(nextFile, nextSource)
  }
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pastedFile = [...event.clipboardData.items]
      .find((item) => item.kind === 'file' && PAYMENT_RECEIPT_TYPES.includes(item.type))
      ?.getAsFile()
    if (!pastedFile) return
    event.preventDefault()
    const extension =
      pastedFile.type === 'image/jpeg' ? 'jpg' : pastedFile.type.split('/')[1] || 'png'
    const namedFile = pastedFile.name
      ? pastedFile
      : new File([pastedFile], `pasted-receipt-${Date.now()}.${extension}`, {
          type: pastedFile.type,
        })
    selectFile(namedFile, 'clipboard_paste')
  }
  return (
    <div className="space-y-2 sm:col-span-2">
      <Label>{label}</Label>
      <div
        role="button"
        tabIndex={0}
        aria-label="Add payment receipt"
        className="rounded-lg border border-dashed px-4 py-4 text-center text-sm outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault()
          selectFile(event.dataTransfer.files[0] ?? null, 'manual_upload')
        }}
        onPaste={handlePaste}
      >
        <UploadCloud className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
        Choose, drop, or paste a receipt
        <span className="mt-1 block text-xs text-muted-foreground">PDF or image, up to 10 MB</span>
      </div>
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
        onChange={(event) => {
          selectFile(event.target.files?.[0] ?? null, 'manual_upload')
          event.target.value = ''
        }}
      />
      {file && (
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-2 text-sm">
          <div className="min-w-0">
            <p className="truncate font-medium">{file.name}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(file.size)}
              {source === 'clipboard_paste' ? ' · Pasted screenshot' : ''}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove ${file.name}`}
            onClick={() => onChange(null, 'manual_upload')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

/** Attach a receipt to a payment already recorded, such as one the email AI recorded without its file (USA-1669). */
function AddReceiptButton({
  paymentId,
  upload,
}: {
  paymentId: number
  upload: ReturnType<typeof useInvoicePayment>['uploadPaymentReceipt']
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <LoadingButton
        type="button"
        variant="ghost"
        size="sm"
        loading={upload.isPending && upload.variables?.paymentId === paymentId}
        loadingText="Adding..."
        onClick={() => inputRef.current?.click()}
      >
        <Paperclip className="h-3.5 w-3.5" /> Add receipt
      </LoadingButton>
      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        aria-label="Receipt file to add"
        accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null
          event.target.value = ''
          if (!file) return
          const error = validatePaymentReceipt(file)
          if (error) {
            toast.error(error)
            return
          }
          upload.mutate(
            { paymentId, file, source: 'manual_upload' },
            {
              onSuccess: () => toast.success('Receipt added'),
              onError: (uploadError) =>
                toast.error(getApiErrorMessage(uploadError, 'Receipt upload failed')),
            },
          )
        }}
      />
    </>
  )
}

export function PaymentHistory({
  invoice,
  payment,
}: {
  invoice: ApInvoiceDetail
  payment: ReturnType<typeof useInvoicePayment>
}) {
  const { setReceiptPreview, downloadPaymentReceipt, openPaymentCorrection, uploadPaymentReceipt } =
    payment
  if (!invoice.payments?.length) return null
  return (
    <ul aria-label="Payment history" className="divide-y rounded-lg border">
      {invoice.payments.map((payment) => (
        <li
          key={payment.id}
          className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 text-sm ${payment.payment_status === 'voided' ? 'text-muted-foreground' : ''}`}
        >
          <div className="min-w-[12rem] flex-1">
            <p className="font-medium">
              {formatMoney(payment.paid_amount)}{' '}
              <span className="font-normal text-muted-foreground">
                paid {compactDate(payment.paid_at || payment.created_at)}
                {payment.payment_method ? ` · ${payment.payment_method}` : ''}
                {payment.receipt_reference || payment.receipt_raw
                  ? ` · ${payment.receipt_reference || payment.receipt_raw}`
                  : ''}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              {payment.recorded_by || 'Recorded user unavailable'}
              {payment.voided_by && ` · Voided by ${payment.voided_by}`}
              {payment.void_reason && ` · ${payment.void_reason}`}
            </p>
          </div>
          {payment.payment_status === 'voided' && <Badge variant="outline">Voided</Badge>}
          {payment.receipt_attachments?.map((receipt) => (
            <div key={receipt.id} className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReceiptPreview({ payment, receipt })}
              >
                <Eye className="h-3.5 w-3.5" /> Receipt
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Download ${receipt.file_name}`}
                onClick={() => void downloadPaymentReceipt(payment, receipt)}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {payment.payment_status !== 'voided' && (
            <AddReceiptButton paymentId={payment.id} upload={uploadPaymentReceipt} />
          )}
          {payment.payment_status !== 'voided' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => openPaymentCorrection(payment)}
            >
              Correct
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}

export function PaymentDialogs({ payment }: { payment: ReturnType<typeof useInvoicePayment> }) {
  const {
    paymentOpen,
    setPaymentOpen,
    paymentForm,
    setPaymentForm,
    paymentToReplace,
    voidReason,
    setVoidReason,
    paymentReceipt,
    setPaymentReceipt,
    paymentReceiptSource,
    setPaymentReceiptSource,
    receiptPreview,
    setReceiptPreview,
    receiptPreviewUrl,
    receiptPreviewError,
    recordPayment,
    replacePayment,
    uploadPaymentReceipt,
    submitPayment,
    downloadPaymentReceipt,
  } = payment
  return (
    <>
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{paymentToReplace ? 'Correct payment' : 'Record payment'}</DialogTitle>
            {paymentToReplace ? (
              <DialogDescription className="sr-only">
                Void the selected payment and save its replacement.
              </DialogDescription>
            ) : (
              <DialogDescription>
                Record a payment entry for this invoice. This saves an accounting record; it does
                not send money.
              </DialogDescription>
            )}
          </DialogHeader>
          {paymentToReplace && (
            <div className="space-y-3 rounded-lg border bg-muted/20 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted-foreground">Original payment</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(paymentToReplace.paid_amount)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {compactDate(paymentToReplace.paid_at || paymentToReplace.created_at)} ·{' '}
                {paymentToReplace.payment_method || 'Method not set'} ·{' '}
                {paymentToReplace.receipt_reference || 'Reference not set'}
              </p>
              <div className="space-y-2">
                <Label htmlFor="void-reason">Reason for correction</Label>
                <ComposeTextarea
                  id="void-reason"
                  value={voidReason}
                  onChange={(event) => setVoidReason(event.target.value)}
                  placeholder="What was incorrect?"
                />
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={paymentForm.payment_status}
                onValueChange={(paymentStatus) =>
                  setPaymentForm((current) => ({
                    ...current,
                    payment_status: paymentStatus as ApInvoicePaymentInput['payment_status'],
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {formatStatus(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paid-amount">Amount</Label>
              <InlineFieldInput
                id="paid-amount"
                inputMode="decimal"
                value={String(paymentForm.paid_amount ?? '')}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    paid_amount: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paid-at">Payment date</Label>
              <InlineFieldInput
                id="paid-at"
                type="datetime-local"
                value={paymentForm.paid_at ?? ''}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    paid_at: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="method">Method</Label>
              <InlineFieldInput
                id="method"
                value={paymentForm.payment_method ?? ''}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    payment_method: event.target.value,
                  }))
                }
                placeholder="Portal, check, wire note"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="reference">Reference</Label>
              <InlineFieldInput
                id="reference"
                value={paymentForm.receipt_reference ?? ''}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    receipt_reference: event.target.value,
                  }))
                }
                placeholder="Check or transaction reference"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="payment-note">Notes</Label>
              <InlineFieldTextarea
                id="payment-note"
                value={paymentForm.receipt_raw ?? ''}
                onChange={(event) =>
                  setPaymentForm((current) => ({
                    ...current,
                    receipt_raw: event.target.value,
                  }))
                }
                placeholder="Optional payment note"
              />
            </div>
            <PaymentReceiptInput
              file={paymentReceipt}
              source={paymentReceiptSource}
              onChange={(nextFile, nextSource) => {
                setPaymentReceipt(nextFile)
                setPaymentReceiptSource(nextSource)
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentOpen(false)}>
              Cancel
            </Button>
            <LoadingButton
              loading={
                recordPayment.isPending ||
                replacePayment.isPending ||
                uploadPaymentReceipt.isPending
              }
              loadingText={
                uploadPaymentReceipt.isPending
                  ? 'Uploading receipt...'
                  : paymentToReplace
                    ? 'Correcting...'
                    : 'Recording...'
              }
              onClick={submitPayment}
            >
              <ReceiptText className="mr-2 h-4 w-4" />{' '}
              {paymentToReplace ? 'Void and replace' : 'Record payment'}
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receiptPreview} onOpenChange={(open) => !open && setReceiptPreview(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {receiptPreview?.receipt.content_type.startsWith('image/') ? (
                <FileImage className="h-4 w-4" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              {receiptPreview?.receipt.file_name || 'Payment receipt'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Authenticated payment receipt preview
            </DialogDescription>
          </DialogHeader>
          {receiptPreviewError ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 p-4 text-sm text-destructive"
            >
              {receiptPreviewError}
            </div>
          ) : receiptPreviewUrl && receiptPreview ? (
            receiptPreview.receipt.content_type.startsWith('image/') ? (
              <img
                src={receiptPreviewUrl}
                alt={receiptPreview.receipt.file_name}
                className="max-h-[70vh] w-full rounded-lg border object-contain"
              />
            ) : (
              <iframe
                title={`${receiptPreview.receipt.file_name} preview`}
                src={receiptPreviewUrl}
                className="h-[70vh] w-full rounded-lg border"
              />
            )
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading receipt...
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptPreview(null)}>
              Close
            </Button>
            {receiptPreview && (
              <Button
                onClick={() =>
                  void downloadPaymentReceipt(receiptPreview.payment, receiptPreview.receipt)
                }
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
